import { invariant } from "@/utils"

export type CheckResponse =
  | {
      result: "success"
      refreshToken: string
    }
  | {
      result: "error"
      error: string
    }
  | {
      result: "cancelled"
    }

export type OpenAuth = (
  getAuthUrl: () => Promise<string>,
  onClose: (response: CheckResponse) => void,
) => unknown

type AuthResultMessage = {
  type: string
  state?: string
} & CheckResponse

export const windowMessageType = "oauthResult"

let current: AbortController | undefined

const openAuth: OpenAuth = async (getUrl, onClose) => {
  current?.abort()
  const controller = new AbortController()
  current = controller

  const authUrl = await getUrl()
  if (controller.signal.aborted) {
    return
  }

  const state = new URL(authUrl).searchParams.get("state")
  invariant(state, "The authorization URL carries no state")

  window.addEventListener(
    "message",
    ({ origin, data }: MessageEvent<AuthResultMessage>) => {
      // The origin alone would pass a forged /oauth-result URL loaded into the
      // popup; only this flow's state proves the result is ours.
      if (
        origin !== window.location.origin ||
        data.type !== windowMessageType ||
        data.state !== state
      ) {
        return
      }

      controller.abort()
      onClose(data)
    },
    { signal: controller.signal },
  )

  const width = 500
  const height = 660
  const left = window.screenX + (window.outerWidth - width) / 2
  const top = window.screenY + (window.outerHeight - height) / 2.5

  window.open(
    authUrl,
    "oauth",
    `toolbar=no,menubar=no,width=${width},height=${height},top=${top},left=${left}`,
  )
}

export default openAuth
